import { useEffect, useMemo, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { api } from '../lib/api';
import { fmtNumber, fmtPercent } from '../lib/format';
import { SectionHeader } from '../components/ui';

type Metric = 'open_rate' | 'click_through_rate' | 'subscriber_count' | 'sends';

const METRIC_OPTIONS: { id: Metric; label: string; format: 'percent' | 'number' }[] = [
  { id: 'open_rate', label: 'Open rate', format: 'percent' },
  { id: 'click_through_rate', label: 'Click-through rate', format: 'percent' },
  { id: 'subscriber_count', label: 'List size', format: 'number' },
  { id: 'sends', label: 'Sends', format: 'number' },
];

const LINE_COLORS = ['#3b82f6', '#ec4899', '#22c55e', '#f59e0b', '#a855f7', '#06b6d4'];

interface ListRow {
  list_id: string;
  list_name: string;
  week_ending: string;
  sends: number | null;
  unique_opens: number | null;
  unique_clicks: number | null;
  open_rate: number | null;
  click_through_rate: number | null;
  subscriber_count: number | null;
  top_subject_line: string | null;
  top_subject_open_rate: number | null;
}

export default function NewsletterPanel() {
  const [lists, setLists] = useState<ListRow[]>([]);
  const [trends, setTrends] = useState<Map<string, ListRow[]>>(new Map());
  const [metric, setMetric] = useState<Metric>('open_rate');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api
      .newslettersLists()
      .then(async (d) => {
        const ls = d.lists as unknown as ListRow[];
        setLists(ls);
        const trendResults = await Promise.all(
          ls.map((l) => api.newslettersListTrend(l.list_id, 12))
        );
        const trendMap = new Map<string, ListRow[]>();
        trendResults.forEach((r) =>
          trendMap.set(r.list_id, r.snapshots as unknown as ListRow[])
        );
        setTrends(trendMap);
      })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, []);

  const chartData = useMemo(() => {
    const allWeeks = new Set<string>();
    for (const rows of trends.values()) {
      for (const r of rows) allWeeks.add(r.week_ending);
    }
    const weeksAsc = [...allWeeks].sort();
    return weeksAsc.map((wk) => {
      const point: Record<string, string | number | null> = { week: wk };
      for (const list of lists) {
        const row = trends.get(list.list_id)?.find((r) => r.week_ending === wk);
        point[list.list_name] = (row?.[metric] as number | null) ?? null;
      }
      return point;
    });
  }, [trends, lists, metric]);

  const metricCfg = METRIC_OPTIONS.find((m) => m.id === metric)!;
  const fmtMetric = (v: number | null | undefined) =>
    v == null ? '—' : metricCfg.format === 'percent' ? fmtPercent(v) : fmtNumber(v);

  const topCampaigns = useMemo(
    () =>
      [...lists]
        .filter((l) => l.top_subject_line && (l.top_subject_open_rate ?? 0) > 0)
        .sort((a, b) => (b.top_subject_open_rate ?? 0) - (a.top_subject_open_rate ?? 0))
        .slice(0, 3),
    [lists]
  );

  const dqFlags = useMemo(() => {
    const flags: { list_name: string; reasons: string[] }[] = [];
    for (const l of lists) {
      const reasons: string[] = [];
      if (l.subscriber_count != null && l.subscriber_count < 100) {
        reasons.push(`only ${l.subscriber_count} subscribers`);
      }
      if (l.open_rate != null && l.open_rate > 0.8) {
        reasons.push(`open rate ${fmtPercent(l.open_rate)} (likely test send)`);
      }
      if (reasons.length > 0) flags.push({ list_name: l.list_name, reasons });
    }
    return flags;
  }, [lists]);

  if (loading) {
    return <div className="text-xs text-white/40 py-12 text-center">Loading newsletters…</div>;
  }
  if (error) {
    return (
      <div className="raised-card text-xs text-white/60">
        Couldn't load newsletter data: {error}
      </div>
    );
  }
  if (lists.length === 0) {
    return (
      <div className="raised-card text-xs text-white/60">
        No newsletter data yet. Set <code>AC_NEWSLETTER_LISTS</code> on the Worker and run the cron.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <SectionHeader title="Newsletter" hint="per-list performance, last 7 days" />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {lists.map((l, i) => {
          const prev = trends.get(l.list_id)?.[1];
          const subDelta =
            l.subscriber_count != null && prev?.subscriber_count != null
              ? l.subscriber_count - prev.subscriber_count
              : null;
          const openRateDelta =
            l.open_rate != null && prev?.open_rate != null
              ? l.open_rate - prev.open_rate
              : null;
          return (
            <div className="raised-card" key={l.list_id}>
              <div className="flex items-center gap-2 mb-2">
                <span
                  className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ background: LINE_COLORS[i % LINE_COLORS.length] }}
                />
                <div className="text-xs text-white/70 truncate">{l.list_name}</div>
              </div>
              <div className="text-2xl font-medium">{fmtNumber(l.subscriber_count)}</div>
              <div className="text-xs text-white/40 mb-3">
                subscribers
                {subDelta != null && subDelta !== 0 && (
                  <span className={subDelta > 0 ? 'delta-up ml-1.5' : 'delta-down ml-1.5'}>
                    {subDelta > 0 ? '▲' : '▼'} {fmtNumber(Math.abs(subDelta))}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div>
                  <div className="text-white/50">Sends</div>
                  <div className="text-sm font-medium text-white/90">{fmtNumber(l.sends)}</div>
                </div>
                <div>
                  <div className="text-white/50">Open</div>
                  <div className="text-sm font-medium text-white/90">{fmtPercent(l.open_rate)}</div>
                  {openRateDelta != null && openRateDelta !== 0 && (
                    <div className={openRateDelta > 0 ? 'delta-up' : 'delta-down'}>
                      {openRateDelta > 0 ? '▲' : '▼'}{' '}
                      {(Math.abs(openRateDelta) * 100).toFixed(1)}pp
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-white/50">CTR</div>
                  <div className="text-sm font-medium text-white/90">
                    {fmtPercent(l.click_through_rate)}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {chartData.length >= 1 && (
        <div className="raised-card">
          <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
            <div className="text-sm font-medium">12-week trend</div>
            <select
              value={metric}
              onChange={(e) => setMetric(e.target.value as Metric)}
              className="bg-white/5 border border-border-subtle rounded px-2 py-1 text-xs text-white/80 focus:outline-none focus:border-white/40"
            >
              {METRIC_OPTIONS.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>
          <div style={{ width: '100%', height: 220 }}>
            <ResponsiveContainer>
              <LineChart data={chartData} margin={{ top: 4, right: 8, bottom: 4, left: -8 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                <XAxis
                  dataKey="week"
                  tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v: number) =>
                    metricCfg.format === 'percent'
                      ? `${(v * 100).toFixed(0)}%`
                      : v >= 1000
                      ? `${Math.round(v / 1000)}k`
                      : String(v)
                  }
                />
                <Tooltip
                  contentStyle={{
                    background: '#161616',
                    border: '1px solid rgba(255,255,255,0.16)',
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  formatter={(v: number) => fmtMetric(v)}
                />
                <Legend
                  wrapperStyle={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}
                  iconType="line"
                />
                {lists.map((l, i) => (
                  <Line
                    key={l.list_id}
                    type="monotone"
                    dataKey={l.list_name}
                    stroke={LINE_COLORS[i % LINE_COLORS.length]}
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {topCampaigns.length > 0 && (
        <div className="raised-card">
          <SectionHeader title="Top campaigns this week" hint="by open rate" />
          <table className="w-full text-sm">
            <thead>
              <tr className="text-white/40 text-xs border-b border-border-subtle">
                <th className="text-left font-normal pb-2">List</th>
                <th className="text-left font-normal pb-2">Subject</th>
                <th className="text-right font-normal pb-2">Open rate</th>
              </tr>
            </thead>
            <tbody>
              {topCampaigns.map((c) => (
                <tr key={c.list_id} className="border-b border-border-subtle/50 last:border-0">
                  <td className="py-2.5 text-white/70">{c.list_name}</td>
                  <td className="py-2.5">{c.top_subject_line}</td>
                  <td className="text-right py-2.5">{fmtPercent(c.top_subject_open_rate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {dqFlags.length > 0 && (
        <div className="raised-card border border-amber-500/30 bg-amber-500/5">
          <div className="text-xs font-medium text-amber-300 mb-2">Data quality flags</div>
          <ul className="text-xs text-white/70 space-y-1">
            {dqFlags.map((f) => (
              <li key={f.list_name}>
                <span className="font-medium">{f.list_name}</span>: {f.reasons.join('; ')}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
